/**
 * User Rota Repository
 * CRUD for user_routa (user–store weekly day/shift assignments)
 */

import { number, string } from 'valibot'
import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'

export interface UserRouta {
    id: number
    tenant_id: number
    user_id: number
    store_id: number
    client_id?: number | null
    sat: number
    sat_shift: number
    sat_visit_type_id?: number | null
    sat_visit_type?: { id: number; en_name: string } | null
    sun: number
    sun_shift: number
    sun_visit_type_id?: number | null
    sun_visit_type?: { id: number; en_name: string } | null
    mon: number
    mon_shift: number
    mon_visit_type_id?: number | null
    mon_visit_type?: { id: number; en_name: string } | null
    tue: number
    tue_shift: number
    tue_visit_type_id?: number | null
    tue_visit_type?: { id: number; en_name: string } | null
    wed: number
    wed_shift: number
    wed_visit_type_id?: number | null
    wed_visit_type?: { id: number; en_name: string } | null
    thu: number
    thu_shift: number
    thu_visit_type_id?: number | null
    thu_visit_type?: { id: number; en_name: string } | null
    fri: number
    fri_shift: number
    fri_visit_type_id?: number | null
    fri_visit_type?: { id: number; en_name: string } | null
    created_by?: number | null
    updated_by?: number | null
    created_at: string
    updated_at: string
    client?: { id: number; en_name: string | null } | null
    user?: {
        id: number
        en_name: string
        username?: string
        reportto?: {
            id: number
            en_name?: string | null
            username?: string | null
        } | null
        role?: {
            id: number
            en_name: string
        } | null
    }
    store?: {
        id: number
        en_name: string
        region?: { id: number; en_name: string } | null
        city?: { id: number; en_name: string } | null
        store_format?: { id: number; en_name: string } | null
        chain?: { id: number; en_name: string } | null
    }
}

export interface CreateUserRoutaRequest {
    tenant_id?: number
    user_id: number
    store_id: number
    client_id?: number | null
    sat?: number
    sat_shift?: number
    sat_visit_type_id?: number | null
    sun?: number
    sun_shift?: number
    sun_visit_type_id?: number | null
    mon?: number
    mon_shift?: number
    mon_visit_type_id?: number | null
    tue?: number
    tue_shift?: number
    tue_visit_type_id?: number | null
    wed?: number
    wed_shift?: number
    wed_visit_type_id?: number | null
    thu?: number
    thu_shift?: number
    thu_visit_type_id?: number | null
    fri?: number
    fri_shift?: number
    fri_visit_type_id?: number | null
}

export interface UpdateUserRoutaRequest extends Partial<CreateUserRoutaRequest> { }

class UserRoutaRepository {
    async getAll(
        page: number = 1,
        limit: number = 10,
        filters?: { user_id?: number; store_id?: number; region_id?: number; city_id?: number }
    ): Promise<PaginatedResponse<UserRouta>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })
        if (filters?.user_id != null) params.append('user_id', String(filters.user_id))
        if (filters?.store_id != null) params.append('store_id', String(filters.store_id))
        if (filters?.region_id != null) params.append('region_id', String(filters.region_id))
        if (filters?.city_id != null) params.append('city_id', String(filters.city_id))
        return apiClient.get<PaginatedResponse<UserRouta>>(`/user-routa?${params.toString()}`)
    }

    async getById(id: number): Promise<UserRouta> {
        return apiClient.get<UserRouta>(`/user-routa/${id}`)
    }


    async create(data: CreateUserRoutaRequest): Promise<UserRouta> {
        return apiClient.post<UserRouta>('/user-routa', data)
    }

    async update(id: number, data: UpdateUserRoutaRequest): Promise<UserRouta> {
        return apiClient.put<UserRouta>(`/user-routa/${id}`, data)
    }

    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/user-routa/${id}`)
    }

    async exportTemplate(): Promise<Blob> {
        return apiClient.getBlob('/user-routa/export/template')
    }

    async importFromExcel(file: File): Promise<{
        success: boolean
        message: string
        data: {
            success: boolean
            total: number
            created: number
            updated: number
            errors: Array<{ row: number; message: string }>
        }
    }> {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post('/user-routa/import/excel', formData)
    }
}

export const userRoutaRepository = new UserRoutaRepository()
